import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperClass, SwiperOptions } from 'swiper/types';
import 'swiper/css';
import classes from './Slider.module.css';
import { SlickSliderProps } from '../../types';
import Icon from '../Icons/Icon';

const SlickSlider: React.FC<SlickSliderProps> = (props) => {
  const {
    isStaticImage,
    contents,
    handleFilterType,
    filter,
    removeFilterKey,
    labelKey,
    isMultiSelect,
    // setChangeMinMaxValue,
    isNotUpperCase,
    alterSlideConfig,
    isSingleEnable,
    setBackToSelectDiamondPage,
    resetRemovedMinMax,
    exclusionOptions = [],
    defaultSlidesToShow = 4
  } = props;

  const total = contents?.length || 0;
  const ringStyleContents = total >= 7 ? 7 : total;

  const breakpoints = useMemo((): { [width: number]: SwiperOptions } => {
    if (alterSlideConfig) {
      // <=340 -> 2, 341-767 -> 3, >=768 -> ringStyleContents
      return {
        0:   { slidesPerView: 2 },
        341: { slidesPerView: 3 },
        768: { slidesPerView: ringStyleContents || 1 },
      };
    }
    // <=991 -> 3, 992-1199 -> defaultSlidesToShow, >=1200 -> min(total, 4)
    return {
      0:    { slidesPerView: 3 },
      992:  { slidesPerView: defaultSlidesToShow },
      1200: { slidesPerView: total >= 4 ? 4 : total || 1 },
    };
  }, [ alterSlideConfig, ringStyleContents, defaultSlidesToShow, total ]);

  const maxSlidesOnScreen = alterSlideConfig
    ? Math.max(3, ringStyleContents)
    : Math.max(3, defaultSlidesToShow, total >= 4 ? 4 : total);
  const enableLoop = total > maxSlidesOnScreen;

  const swiperRef = useRef<SwiperClass | null>(null);

  const initialSlide = useMemo(() => {
    if (!total || !contents) return 0;
    const selected = Array.isArray(filter[labelKey])
      ? (filter[labelKey] as string[])[0]
      : filter[labelKey];
    const index = contents.findIndex((content) => {
      const code = isNotUpperCase ? content.code : content.code.toUpperCase();
      return isMultiSelect
        ? Array.isArray(filter[labelKey]) && (filter[labelKey] as string[]).includes(code)
        : selected === code;
    });
    return index > 0 ? index : 0;
  }, []);

  const handleArrowPress = useCallback(() => {
    const swiper = swiperRef.current;
    if (!swiper || !swiper.slides) return;
    swiper.slides.forEach((slide: HTMLElement) => {
      const isVisible = slide.classList.contains('swiper-slide-visible');
      slide.querySelectorAll('button').forEach((button) => {
        (button as HTMLButtonElement).tabIndex = isVisible ? 0 : -1;
      });
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Tab") {
        handleArrowPress();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [ handleArrowPress ]);

  const handleShape = (findFilterShape: string | boolean | string[] | undefined, label: string) => {
    resetRemovedMinMax && resetRemovedMinMax();
   /* if (setChangeMinMaxValue) {
      setChangeMinMaxValue(true);
    }*/
    if(setBackToSelectDiamondPage){
      setBackToSelectDiamondPage(false);
    }
    if (isMultiSelect) {
      if (Array.isArray(filter[labelKey]) && findFilterShape) {
        const filterShape = (filter[labelKey] as string[]).filter(f => f !== label);
        if (filterShape.length === 0) {
          return removeFilterKey(labelKey);
        }
        return handleFilterType(filterShape, labelKey)
      }
      if (filter[labelKey]) {
        return handleFilterType([...filter[labelKey] as string | string[], label], labelKey)
      } else {
        return handleFilterType([label], labelKey)
      }
    }
    if (!isSingleEnable) {
      if (findFilterShape) {
        const filterShape = filter[labelKey];
        if (filterShape === label) {
          return removeFilterKey(labelKey);
        }
        return handleFilterType(label, labelKey)
      }
    }
    handleFilterType(label, labelKey);
  }

  return (
    <div className={classes.root}>
      <button
        type={'button'}
        tabIndex={0}
        aria-label="prev button"
        className={`${classes.arrow} ${classes.prevArrowBtn}`}
        onClick={() => swiperRef.current?.slidePrev()}
      >
        <Icon name="hk_chevron_left" className={classes.prevArrow}/>
      </button>
      <Swiper
        className={classes.swiper}
        speed={500}
        loop={enableLoop}
        loopPreventsSliding={false}
        initialSlide={initialSlide}
        watchSlidesProgress={true}
        breakpoints={breakpoints}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          handleArrowPress();
        }}
        onSlideChange={handleArrowPress}
        onBreakpoint={handleArrowPress}
        onResize={handleArrowPress}
      >
        {contents &&
          contents.map((content, index: number) => {
            const findFilterShape = isMultiSelect ? Array.isArray(filter[labelKey]) && (filter[labelKey] as string[]).find(f => f === content.code.toUpperCase()) : Array.isArray(filter[labelKey]) ? (filter[labelKey] as string[])[0] :filter[labelKey];
            return (
              <SwiperSlide key={index}>
                <button
                  className={
                    `${findFilterShape === (isNotUpperCase ? content.code : content.code.toUpperCase())
                      ? `${classes.active} currentActiveSlide p-1`
                      : ''} ${classes.content} p-1`
                  }
                  disabled={exclusionOptions.includes(content.code)}
                  type={"button"}
                  role={"button"}
                  tabIndex={1}
                  onClick={() => handleShape(findFilterShape,isNotUpperCase ? content.code : content.code.toUpperCase())}
                >
                    <div className={classes.img}>
                        {/*{isStaticImage ? <img src={content.imgPath}/> : null}*/}
                        {/*<Vector name={content.label?.toLowerCase()}/>*/}
                        <img
                            src={content.imgPath}
                            alt={content.label}
                            /*style={{
                              WebkitMaskImage: `url(${content.imgPath})`,
                              maskImage: `url(${content.imgPath})`,
                              maskSize: 'contain'
                          }}*/
                        />
                    </div>

                  <div className={classes.label}>{content.label}</div>
                </button>
              </SwiperSlide>
            );
          })}
      </Swiper>
      <button
        type={'button'}
        tabIndex={0}
        aria-label="Next"
        className={`${classes.arrow} ${classes.nextArrowBtn}`}
        onClick={() => swiperRef.current?.slideNext()}
      >
        <Icon name="hk_chevron_right" className={classes.nextArrow}/>
      </button>
    </div>
  );
};
export default SlickSlider;
